<template>
  <e-sidebar
    id="sidebar-auto-delivery-monitor-sale"
    :title="saleId ? `${$t('Venda')} #${saleId} - ${$t('Entrega automática')}` : ''"
    :show="!!saleId"
    :fetching="fetching"
    :hide-save-button="true"
    :cancel-button-text="$t('Fechar')"
    :no-close-on-backdrop="false"
    width="45%"
    @hidden="$emit('hidden')"
  >
    <template #content>
      <div v-if="detail">
        <h3 class="h5">
          {{ $t('Resumo') }}
        </h3>
        <dl class="monitor-detail__list">
          <dt>{{ $t('Loja') }}</dt>
          <dd>{{ sale.storeName || `#${sale.storeId}` }}</dd>
          <dt>{{ $t('Cliente') }}</dt>
          <dd>{{ detail.customerName || '-' }}</dd>
          <dt>{{ $t('Valor') }}</dt>
          <dd>{{ formatCurrency(detail.netValue) }}</dd>
          <dt>{{ $t('Criada em') }}</dt>
          <dd>{{ formatDateTime(sale.createdDate) }}</dd>
          <dt>{{ $t('Entrega prevista') }}</dt>
          <dd>{{ formatDateTime(detail.deliveryDate) }}</dd>
          <dt>{{ $t('Estado automático') }}</dt>
          <dd>
            <b-badge :variant="variant(autoDeliveryStateLabels, sale.autoDeliveryState)">
              {{ $t(label(autoDeliveryStateLabels, sale.autoDeliveryState)) }}
            </b-badge>
            <span v-if="sale.ineligibilityReason">
              — {{ $t(label(ineligibilityReasonLabels, sale.ineligibilityReason)) }}
            </span>
          </dd>
          <dt>{{ $t('Entrega') }}</dt>
          <dd>{{ $t(label(deliveryStatusLabels, sale.deliveryStatus)) }}</dd>
          <dt>{{ $t('Motoboy próprio') }}</dt>
          <dd>{{ sale.motoboyName || '-' }}</dd>
          <dt v-if="sale.alerts.length">
            {{ $t('Alertas') }}
          </dt>
          <dd v-if="sale.alerts.length">
            <b-badge
              v-for="alert in sale.alerts"
              :key="alert"
              variant="light-danger"
              class="mr-25"
            >
              {{ $t(label(alertLabels, alert)) }}
            </b-badge>
          </dd>
        </dl>

        <h3 class="h5 mt-2">
          {{ $t('Work') }}
        </h3>
        <p
          v-if="!detail.works.length"
          class="text-muted"
        >
          {{ $t('Nenhum work.') }}
        </p>
        <dl
          v-for="work in detail.works"
          :key="work.id"
          class="monitor-detail__list monitor-detail__block"
        >
          <dt>{{ $t('Geração') }}</dt>
          <dd>{{ work.generation }}</dd>
          <dt>{{ $t('Estado') }}</dt>
          <dd>
            <b-badge :variant="variant(workStateLabels, work.state)">
              {{ $t(label(workStateLabels, work.state)) }}
            </b-badge>
          </dd>
          <dt>{{ $t('Disponível em') }}</dt>
          <dd>{{ formatDateTime(work.availableAt) }}</dd>
          <dt>{{ $t('Tentativas') }}</dt>
          <dd>{{ work.attemptCount }}</dd>
          <dt>{{ $t('Lease') }}</dt>
          <dd>{{ work.leaseOwner ? `${work.leaseOwner} até ${formatDateTime(work.leaseExpiresAt)}` : '-' }}</dd>
          <dt>{{ $t('Último erro') }}</dt>
          <dd :class="{ 'text-danger': work.lastErrorCode }">
            {{ work.lastErrorCode || '-' }}
          </dd>
          <dt>{{ $t('Criado / concluído') }}</dt>
          <dd>{{ formatDateTime(work.createdDate) }} / {{ formatDateTime(work.completedAt) }}</dd>
        </dl>

        <h3 class="h5 mt-2">
          {{ $t('Fornecedores acionados') }}
        </h3>
        <p
          v-if="!sale.dispatches.length"
          class="text-muted"
        >
          {{ $t('Nenhum fornecedor foi acionado.') }}
        </p>
        <dl
          v-for="dispatch in sale.dispatches"
          :key="dispatch.id"
          class="monitor-detail__list monitor-detail__block"
          :class="{ 'monitor-detail__block--winner': dispatch.isAccepted }"
        >
          <dt>{{ $t('Fornecedor') }}</dt>
          <dd>
            {{ dispatch.deliverySupplierName || `#${dispatch.deliverySupplierId}` }}
            <b-badge
              v-if="dispatch.isAccepted"
              variant="success"
              class="ml-25"
            >
              {{ $t('Vencedor') }}
            </b-badge>
          </dd>
          <dt>{{ $t('Execução') }}</dt>
          <dd>
            <b-badge :variant="variant(dispatchStateLabels, dispatch.executionState)">
              {{ $t(label(dispatchStateLabels, dispatch.executionState)) }}
            </b-badge>
          </dd>
          <dt>{{ $t('Status no fornecedor') }}</dt>
          <dd>{{ dispatch.supplierStatus || '-' }}</dd>
          <dt>{{ $t('Pedido no fornecedor') }}</dt>
          <dd>{{ dispatch.supplierOrderId || '-' }}</dd>
          <dt>{{ $t('Tentativas') }}</dt>
          <dd>
            {{ dispatch.attemptCount }}
            <span v-if="dispatch.nextAttemptAt">
              ({{ $t('próxima') }}: {{ formatDateTime(dispatch.nextAttemptAt) }})
            </span>
          </dd>
          <dt>{{ $t('Solicitado / respondido') }}</dt>
          <dd>{{ formatDateTime(dispatch.requestedAt) }} / {{ formatDateTime(dispatch.respondedAt) }}</dd>
          <dt v-if="dispatch.failureReason || dispatch.lastErrorCode">
            {{ $t('Falha') }}
          </dt>
          <dd
            v-if="dispatch.failureReason || dispatch.lastErrorCode"
            class="text-danger"
          >
            {{ [dispatch.failureReason, dispatch.lastErrorCode].filter(Boolean).join(' · ') }}
          </dd>
        </dl>

        <h3 class="h5 mt-2">
          {{ $t('Linha do tempo') }}
        </h3>
        <p
          v-if="!detail.timeline.length"
          class="text-muted"
        >
          {{ $t('Nenhum evento registrado.') }}
        </p>
        <ul class="monitor-detail__timeline list-unstyled mb-0">
          <li
            v-for="(entry, index) in detail.timeline"
            :key="index"
          >
            <small class="text-muted">{{ formatDateTime(entry.at) }}</small>
            <strong class="d-block">
              {{ entry.title }}
              <b-badge
                v-if="entry.published === false"
                variant="danger"
                class="ml-25"
              >
                {{ $t('não publicado') }}
              </b-badge>
            </strong>
            <small
              v-if="entry.details"
              class="d-block"
            >{{ entry.details }}</small>
            <small
              v-if="entry.lastErrorCode"
              class="d-block text-danger"
            >{{ entry.lastErrorCode }}</small>
          </li>
        </ul>
      </div>
    </template>
  </e-sidebar>
</template>

<script>
import { BBadge } from 'bootstrap-vue'
import { mapActions } from 'vuex'
import ESidebar from '@/views/components/ESidebar.vue'
import {
  alertLabels,
  autoDeliveryStateLabels,
  deliveryStatusLabels,
  dispatchStateLabels,
  formatDateTime,
  ineligibilityReasonLabels,
  label,
  variant,
  workStateLabels,
} from '../monitor-labels'

export default {
  name: 'MonitorSaleSidebar',

  components: { BBadge, ESidebar },

  props: {
    saleId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      detail: null,
      fetching: false,
      alertLabels,
      autoDeliveryStateLabels,
      deliveryStatusLabels,
      dispatchStateLabels,
      ineligibilityReasonLabels,
      workStateLabels,
    }
  },

  computed: {
    sale() {
      return this.detail?.sale
    },
  },

  watch: {
    saleId: {
      immediate: true,
      handler(value) {
        this.detail = null
        if (value) this.load(value)
      },
    },
  },

  methods: {
    ...mapActions('pages/sale/autoDeliveryMonitor', ['fetchSaleDetail']),
    label,
    variant,
    formatDateTime,
    formatCurrency(value) {
      return (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    async load(saleId) {
      this.fetching = true
      try {
        const detail = await this.fetchSaleDetail(saleId)
        // Ignora a resposta de uma venda que já não é a aberta.
        if (String(saleId) === String(this.saleId)) this.detail = detail
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.monitor-detail {
  &__list {
    display: grid;
    grid-template-columns: minmax(140px, max-content) 1fr;
    gap: 0.25rem 1rem;
    margin-bottom: 0.5rem;

    dt {
      font-weight: 500;
    }

    dd {
      margin: 0;
      word-break: break-word;
    }
  }

  &__block {
    padding: 0.5rem 0.75rem;
    border: 1px solid #ebe9f1;
    border-radius: 0.5rem;

    &--winner {
      border-color: var(--success);
    }
  }

  &__timeline li {
    padding: 0.5rem 0 0.5rem 0.75rem;
    border-left: 2px solid #ebe9f1;
  }
}
</style>
